import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { BrandMark } from "../components/BrandMark";
import { PublicMobileMenu } from "../components/PublicMobileMenu";
import { toast } from "sonner";
import {
    Menu, X, ArrowRight, Info, CheckCircle2, TrendingUp, Clock,
    Wallet, ShieldCheck, UserCheck, Ban, XCircle,
    Facebook, Instagram, Linkedin, MessageCircle,
} from "lucide-react";

/* ---------- nav ---------- */
const NAV = [
    { label: "Home", to: "/home" },
    { label: "About Us", to: "/about-us" },
    { label: "Loans", to: "/loans" },
    { label: "Saving Scheme", to: "/daily-savings" },
    { label: "Investment", to: "/investment" },
    { label: "Careers", to: "/careers" },
    { label: "Contact Us", to: "/contact-us" },
];

const KEY_TERMS = [
    { icon: Wallet, title: "One-time lump-sum investment", desc: "Invest a single amount upfront to open the account." },
    { icon: Clock, title: "Fixed tenure — 365 days", desc: "The investment runs for exactly 365 days from approval." },
    { icon: XCircle, title: "Before 365 days", desc: "Full premature withdrawal is allowed. Only principal is returned — 0% interest." },
    { icon: TrendingUp, title: "At 365 days", desc: "12% p.a. simple interest and maturity of the account." },
    { icon: Ban, title: "No partial withdrawal", desc: "Only a full closure is supported before maturity." },
    { icon: Ban, title: "No post-maturity interest", desc: "Interest accrual stops at 365 days; no further interest is paid." },
];

const FOOTER_LINKS = {
    Products: ["Personal Loan", "Business Loan", "Group Loan", "Saving Scheme", "Investment"],
    Company: ["About Us", "Careers", "News & Updates", "Contact Us"],
    Support: ["FAQs", "Grievance Redressal", "Customer Care", "Downloads"],
};

const placeholderLoginToast = () => {
    window.location.assign("/customer-login");
};

const inr = (n) =>
    new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.max(0, Math.round(n)));

/* ---------- page ---------- */
export default function PublicInvestment() {
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);

    /* illustrative-only calculator (no backend) */
    const [principal, setPrincipal] = useState(100000);

    const preview = useMemo(() => {
        const p = Math.max(0, Number(principal) || 0);
        // Simple interest at 12% p.a. for 365 days (illustrative)
        const interest = (p * 12 * 1) / 100;
        return { principal: p, interest, maturity: p + interest };
    }, [principal]);

    useEffect(() => {
        document.title = "Investment | Lokdhara Microfinance";
        window.scrollTo({ top: 0, behavior: "auto" });
    }, []);

    const go = (to) => { if (to) navigate(to); };

    return (
        <div id="top" className="min-h-screen bg-white text-slate-900 antialiased">
            {/* HEADER ---------------------------------------------------- */}
            <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between gap-4">
                    <Link to="/home" className="flex items-center" data-testid="inv-brand">
                        <BrandMark size={56} className="sm:!h-14" />
                    </Link>
                    <nav className="hidden lg:flex items-center gap-6 text-sm font-medium text-slate-700">
                        {NAV.map((n) => (
                            <button key={n.label}
                                onClick={() => go(n.to)}
                                className={`hover:text-brand-royal transition ${n.to === "/investment" ? "text-brand-royal" : ""}`}
                                data-testid={`inv-nav-${n.label.replace(/\s+/g, "-").toLowerCase()}`}
                            >
                                {n.label}
                            </button>
                        ))}
                    </nav>
                    <div className="flex items-center gap-2">
                        <Button
                            className="hidden sm:inline-flex bg-brand-royal hover:bg-brand-hover text-white rounded-full h-10 px-5"
                            onClick={placeholderLoginToast}
                            data-testid="inv-header-customer-login"
                        >
                            Customer Login
                        </Button>
                        <button
                            aria-label="Open menu"
                            className="lg:hidden h-10 w-10 rounded-lg border border-slate-200 grid place-items-center"
                            onClick={() => setMenuOpen(true)}
                            data-testid="inv-mobile-menu-btn"
                        >
                            <Menu className="h-5 w-5" />
                        </button>
                    </div>
                </div>
                {menuOpen && (
                    <PublicMobileMenu
                        open={menuOpen}
                        onOpenChange={setMenuOpen}
                        nav={NAV}
                        onNavItemClick={(n) => go(n.to)}
                        onLoginClick={placeholderLoginToast}
                        testId="inv-mobile-menu"
                        activePath="/investment"
                    />
                )}
            </header>

            {/* NAVY HERO ------------------------------------------------ */}
            <section className="relative overflow-hidden bg-brand-navy text-white">
                <div className="absolute inset-0 pointer-events-none opacity-20"
                    style={{ backgroundImage: "radial-gradient(circle at 15% 25%, rgba(255,255,255,0.15) 0%, transparent 45%), radial-gradient(circle at 85% 65%, rgba(255,255,255,0.10) 0%, transparent 40%)" }}
                    aria-hidden
                />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
                    <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-tint bg-white/10 rounded-full px-3 py-1">
                        Investment
                    </span>
                    <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight max-w-3xl">
                        A Defined-Tenure Investment Option
                    </h1>
                    <p className="mt-5 text-base sm:text-lg text-slate-200/90 max-w-2xl leading-relaxed">
                        A single lump-sum, one-year investment with clearly defined maturity and
                        premature-withdrawal rules — subject to approved product terms.
                    </p>
                </div>
            </section>

            {/* KEY TERMS ------------------------------------------------ */}
            <section className="py-20 sm:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-royal bg-brand-soft rounded-full px-3 py-1">
                            Key Terms
                        </span>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-brand-navy tracking-tight">
                            The rules at a glance
                        </h2>
                    </div>
                    <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                        {KEY_TERMS.map(({ icon: Icon, title, desc }, i) => (
                            <Card key={title}
                                className="rounded-2xl border-slate-100 p-6 hover:shadow-card-hover transition"
                                data-testid={`inv-term-${i + 1}`}
                            >
                                <div className="flex items-start gap-4">
                                    <div className="h-11 w-11 rounded-xl bg-brand-soft grid place-items-center shrink-0">
                                        <Icon className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                                    </div>
                                    <div>
                                        <div className="font-semibold text-slate-900">{title}</div>
                                        <p className="mt-1 text-sm text-slate-600 leading-relaxed">{desc}</p>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* MATURITY EXAMPLE ---------------------------------------- */}
            <section className="bg-slate-50 py-20 sm:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 items-start">
                    <div>
                        <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-royal">
                            Maturity Example
                        </span>
                        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-brand-navy tracking-tight leading-tight">
                            Principal + simple interest at 365 days
                        </h2>
                        <p className="mt-5 text-slate-600 leading-relaxed">
                            At the end of 365 days, the maturity value equals the invested principal plus
                            simple interest at 12% p.a. Interest is calculated on the original principal
                            for the full tenure — not compounded.
                        </p>
                        <div className="mt-6 inline-flex items-center gap-2 bg-white border border-brand-soft rounded-full px-4 py-2 text-xs font-semibold text-brand-royal">
                            <Info className="h-3.5 w-3.5" /> Illustrative only
                        </div>
                    </div>

                    <Card className="rounded-3xl border-slate-100 shadow-card p-6 sm:p-7 bg-white"
                        data-testid="inv-maturity-example"
                    >
                        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                            Example
                        </div>
                        <div className="mt-3 grid grid-cols-3 gap-3">
                            <div className="rounded-xl bg-brand-soft p-4">
                                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Principal</div>
                                <div className="mt-1 font-display font-bold text-brand-navy text-lg">₹ 1,00,000</div>
                            </div>
                            <div className="rounded-xl bg-brand-soft p-4">
                                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Rate</div>
                                <div className="mt-1 font-display font-bold text-brand-navy text-lg">12% p.a.</div>
                            </div>
                            <div className="rounded-xl bg-brand-soft p-4">
                                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Tenure</div>
                                <div className="mt-1 font-display font-bold text-brand-navy text-lg">365 days</div>
                            </div>
                        </div>
                        <div className="mt-5 rounded-2xl bg-brand-navy text-white p-5">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-white/70">Simple interest</span>
                                <span className="font-display font-bold text-lg">₹ 12,000</span>
                            </div>
                            <div className="mt-3 border-t border-white/10 pt-4 flex items-center justify-between">
                                <span className="text-sm text-white/70">Maturity value</span>
                                <span className="font-display font-bold text-2xl text-brand-tint">
                                    ₹ 1,12,000
                                </span>
                            </div>
                        </div>
                        <p className="mt-4 text-[11px] leading-relaxed text-slate-500 italic">
                            Illustrative example only. Actual maturity is determined by approved product
                            terms and applicable requirements.
                        </p>
                    </Card>
                </div>
            </section>

            {/* PREMATURE WITHDRAWAL ------------------------------------ */}
            <section className="py-20 sm:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 items-center">
                    <Card className="rounded-2xl border-slate-100 p-7 order-2 lg:order-1"
                        data-testid="inv-premature-card"
                    >
                        <div className="flex items-start gap-4">
                            <div className="h-11 w-11 rounded-xl bg-amber-50 grid place-items-center shrink-0">
                                <XCircle className="h-5 w-5 text-amber-600" strokeWidth={1.75} />
                            </div>
                            <div>
                                <div className="font-semibold text-slate-900">Before 365 days</div>
                                <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                                    A full premature withdrawal is supported. Only the invested principal
                                    is returned — no interest is paid for the period. Partial withdrawal
                                    is not available.
                                </p>
                            </div>
                        </div>
                    </Card>
                    <div className="order-1 lg:order-2">
                        <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-royal bg-brand-soft rounded-full px-3 py-1">
                            Premature Withdrawal
                        </span>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-brand-navy tracking-tight leading-tight">
                            Principal-only payout before maturity
                        </h2>
                        <p className="mt-4 text-slate-600 leading-relaxed">
                            If you need to exit before the 365-day tenure completes, you receive your
                            invested principal back. No interest accrues for the elapsed period.
                        </p>
                    </div>
                </div>
            </section>

            {/* NOMINEE -------------------------------------------------- */}
            <section className="bg-brand-soft py-20 sm:py-24">
                <div className="max-w-5xl mx-auto px-4 sm:px-6">
                    <div className="text-center max-w-2xl mx-auto mb-10">
                        <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-royal">
                            Nominee &amp; Legal Claimant
                        </span>
                        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-brand-navy tracking-tight leading-tight">
                            Structured, document-based settlement
                        </h2>
                    </div>
                    <div className="grid gap-5 md:grid-cols-2">
                        <Card className="rounded-2xl border-white bg-white/95 p-6"
                            data-testid="inv-nominee-verification"
                        >
                            <div className="flex items-start gap-4">
                                <div className="h-11 w-11 rounded-xl bg-brand-soft grid place-items-center shrink-0">
                                    <UserCheck className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                                </div>
                                <div>
                                    <div className="font-semibold text-slate-900">Nominee / legal-claimant verification</div>
                                    <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                                        The registered nominee or legal claimant is verified before any
                                        settlement is initiated.
                                    </p>
                                </div>
                            </div>
                        </Card>
                        <Card className="rounded-2xl border-white bg-white/95 p-6"
                            data-testid="inv-nominee-documents"
                        >
                            <div className="flex items-start gap-4">
                                <div className="h-11 w-11 rounded-xl bg-brand-soft grid place-items-center shrink-0">
                                    <ShieldCheck className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                                </div>
                                <div>
                                    <div className="font-semibold text-slate-900">Document-based settlement</div>
                                    <p className="mt-1 text-sm text-slate-600 leading-relaxed">
                                        Settlement is completed on the basis of required documents, in
                                        line with applicable product rules.
                                    </p>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            </section>

            {/* VISUAL CALCULATOR (placeholder) ------------------------- */}
            <section className="py-20 sm:py-24">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <div className="text-center max-w-2xl mx-auto mb-10">
                        <span className="inline-block text-xs font-semibold uppercase tracking-widest text-brand-royal bg-brand-soft rounded-full px-3 py-1">
                            Illustrative Preview
                        </span>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-brand-navy tracking-tight">
                            Maturity preview
                        </h2>
                        <p className="mt-3 text-sm text-slate-500 italic">
                            For illustration only — actual maturity is determined by approved product
                            terms.
                        </p>
                    </div>

                    <Card className="rounded-3xl border-slate-100 shadow-card p-6 sm:p-8 grid lg:grid-cols-2 gap-8"
                        data-testid="inv-calculator"
                    >
                        {/* inputs */}
                        <div className="space-y-5">
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    Investment amount (₹)
                                </label>
                                <Input
                                    type="number"
                                    min={0}
                                    value={principal}
                                    onChange={(e) => setPrincipal(e.target.value)}
                                    className="mt-2 h-11 rounded-xl"
                                    data-testid="inv-calc-amount"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="rounded-xl border border-slate-200 p-3">
                                    <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Tenure</div>
                                    <div className="mt-1 font-semibold text-brand-navy text-sm">365 days (fixed)</div>
                                </div>
                                <div className="rounded-xl border border-slate-200 p-3">
                                    <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Rate</div>
                                    <div className="mt-1 font-semibold text-brand-navy text-sm">12% p.a. (simple)</div>
                                </div>
                            </div>
                            <div className="flex items-start gap-2 text-xs text-slate-500 italic">
                                <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                                <span>
                                    Interest is calculated on the original principal for the full 365-day
                                    tenure. Not applicable for premature withdrawal.
                                </span>
                            </div>
                        </div>

                        {/* preview */}
                        <div className="rounded-2xl bg-brand-navy text-white p-6 sm:p-7 flex flex-col justify-between"
                            data-testid="inv-calc-preview"
                        >
                            <div className="flex items-center gap-3">
                                <div className="h-11 w-11 rounded-xl bg-white/10 grid place-items-center">
                                    <TrendingUp className="h-5 w-5 text-brand-tint" strokeWidth={1.75} />
                                </div>
                                <div className="text-xs font-semibold uppercase tracking-widest text-brand-tint">
                                    Illustrative Result
                                </div>
                            </div>

                            <div className="mt-6 space-y-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-white/70">Principal</span>
                                    <span className="font-display font-bold text-lg">₹ {inr(preview.principal)}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-white/70">Simple interest (365 d)</span>
                                    <span className="font-display font-bold text-lg">₹ {inr(preview.interest)}</span>
                                </div>
                                <div className="border-t border-white/10 pt-4 flex items-center justify-between">
                                    <span className="text-sm text-white/70">Maturity value</span>
                                    <span className="font-display font-bold text-2xl text-brand-tint">
                                        ₹ {inr(preview.maturity)}
                                    </span>
                                </div>
                            </div>

                            <p className="mt-6 text-[11px] leading-relaxed text-white/60">
                                Illustration only. Actual investment acceptance, maturity value and terms
                                are subject to approval.
                            </p>
                        </div>
                    </Card>
                </div>
            </section>

            {/* REQUIRED NOTE ------------------------------------------ */}
            <section className="pb-16">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <Card className="rounded-2xl border-brand-soft bg-brand-soft/50 p-6 sm:p-7 flex items-start gap-4"
                        data-testid="inv-important-note"
                    >
                        <div className="h-10 w-10 rounded-xl bg-white grid place-items-center shrink-0">
                            <Info className="h-5 w-5 text-brand-royal" strokeWidth={1.75} />
                        </div>
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wider text-brand-royal">
                                Important Note
                            </div>
                            <p className="mt-2 text-slate-700 leading-relaxed">
                                Investment availability, acceptance, maturity value and terms are
                                subject to approval and applicable requirements.
                            </p>
                        </div>
                    </Card>
                </div>
            </section>

            {/* CTA ----------------------------------------------------- */}
            <section className="bg-gradient-to-br from-brand-navy to-brand-royal text-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
                    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
                        Ready to explore a defined-tenure investment?
                    </h2>
                    <p className="mt-4 text-white/80 max-w-xl mx-auto">
                        Speak with our team to review eligibility, documents and applicable terms.
                    </p>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <Button
                            className="bg-white text-brand-navy hover:bg-brand-soft rounded-full h-12 px-6 font-semibold"
                            onClick={() => navigate("/home")}
                            data-testid="inv-cta-contact-btn"
                        >
                            Contact Us <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                        <Button
                            variant="outline"
                            className="rounded-full h-12 px-6 border-white/40 text-white hover:bg-white/10 hover:text-white"
                            onClick={placeholderLoginToast}
                            data-testid="inv-cta-customer-login"
                        >
                            Customer Login
                        </Button>
                    </div>
                </div>
            </section>

            {/* FOOTER -------------------------------------------------- */}
            <footer className="bg-brand-navy text-slate-300">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-8">
                    <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-5">
                        <div className="lg:col-span-2">
                            <BrandMark size={40} variant="white" />
                            <p className="mt-5 text-sm leading-relaxed max-w-md text-slate-400">
                                Lokdhara Microfinance is focused on providing structured and accessible
                                financial services through transparent processes, clear records and
                                customer-oriented support.
                            </p>
                            <div className="mt-5 flex gap-3">
                                {[Facebook, Instagram, Linkedin, MessageCircle].map((Icon, i) => (
                                    <a key={i} href="/home"
                                        className="h-9 w-9 rounded-lg bg-white/10 hover:bg-white/20 grid place-items-center transition"
                                        aria-label={`social-${i}`}
                                    >
                                        <Icon className="h-4 w-4" />
                                    </a>
                                ))}
                            </div>
                        </div>
                        {Object.entries(FOOTER_LINKS).map(([col, items]) => (
                            <div key={col}>
                                <div className="text-white font-semibold text-sm mb-4">{col}</div>
                                <ul className="space-y-2 text-sm">
                                    {items.map((item) => (
                                        <li key={item}>
                                            <button
                                                onClick={() => {
                                                    if (item === "About Us") navigate("/about-us");
                                                    else if (["Personal Loan", "Business Loan", "Group Loan"].includes(item)) navigate("/loans");
                                                    else if (item === "Saving Scheme") navigate("/daily-savings");
                                                    else if (item === "Investment") navigate("/investment");
                                                    else navigate("/home");
                                                }}
                                                className="hover:text-white transition text-left"
                                            >
                                                {item}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    <p className="mt-12 text-xs text-slate-400 leading-relaxed max-w-3xl">
                        Product availability, eligibility, rates and terms are subject to approval and
                        applicable requirements.
                    </p>

                    <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-400">
                        <div>© {new Date().getFullYear()} Lokdhara Microfinance. All rights reserved.</div>
                        <div className="flex items-center gap-5">
                            <button onClick={() => navigate("/home")} className="hover:text-white">Privacy Policy</button>
                            <button onClick={() => navigate("/home")} className="hover:text-white">Terms &amp; Conditions</button>
                            <Link
                                to="/login"
                                className="text-slate-300 hover:text-white underline underline-offset-4"
                                data-testid="inv-footer-staff-login"
                            >
                                Staff Login
                            </Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
